import { api } from "./client";

export type PunchStatus = "PENDING_GPS" | "CONFIRMED" | "EXCEPTION" | "SUPERSEDED";

export interface PunchCandidate {
  assignment_id: string;
  venue_name: string;
  local_time: string;
  role: string | null;
}

export interface PunchView {
  type: "IN" | "OUT";
  first_name: string;
  status: PunchStatus;
  expires_at: string;
  expires_local: string;
  venue_timezone: string;
  consumed_local: string;
  candidates: PunchCandidate[];
  selected_assignment_id: string | null;
  attempts_used: number;
  attempts_max: number;
  last_outcome: string | null;
  exception_codes: string[];
  result: { passed: boolean; distance_m: number | null; accuracy_m: number | null; reason_codes: string[] } | null;
  assistance_requested: boolean;
  outcome: string | null;
}

export interface LocationPayload {
  latitude: number;
  longitude: number;
  accuracy_m: number;
  position_timestamp: string;
  selected_assignment_id?: string;
}

export type FailureReason = "PERMISSION_DENIED" | "POSITION_UNAVAILABLE" | "TIMEOUT" | "UNSUPPORTED";

export interface CorrectionView {
  type: "IN" | "OUT";
  code: string;
  first_name: string;
  sms_received_at: string;
  sms_received_local: string;
  used: boolean;
  status: string;
}

export interface ClockSettings {
  values: Record<string, number | string | boolean | null>;
  specs: Record<string, { min: number | null; max: number | null; description: string; approved_value: number | string }>;
  payable_options: string[];
}

export type Row = Record<string, unknown> & { id: string; employee_name?: string | null; venue_name?: string | null; status?: string; created_at: string };

export const punchApi = {
  get: (t: string) => api.get<PunchView>(`/public/punch/${t}`),
  location: (t: string, body: LocationPayload) => api.post<PunchView>(`/public/punch/${t}/location`, body),
  failure: (t: string, reason: FailureReason) => api.post<PunchView>(`/public/punch/${t}/location-failure`, { reason }),
  assistance: (t: string, note: string) => api.post<PunchView>(`/public/punch/${t}/assistance`, { note: note || null }),
  correction: (t: string) => api.get<CorrectionView>(`/public/correction/${t}`),
  submitCorrection: (t: string, note: string) => api.post<CorrectionView>(`/public/correction/${t}`, { note }),
};

export const clockApi = {
  settings: () => api.get<ClockSettings>("/admin/settings/clock"),
  update: (key: string, value: unknown) => api.put<{ values: ClockSettings["values"] }>(`/admin/settings/clock/${key}`, { value }),
  summary: () => api.get<Record<string, number | boolean>>("/admin/timeclock/summary"),
  list: (kind: "requests" | "sessions" | "exceptions", status?: string) =>
    api.get<Row[]>(`/admin/timeclock/${kind}${status ? `?status=${status}` : ""}`),
  evidence: (requestId: string) => api.get<Row[]>(`/admin/location-evidence?punch_request_id=${requestId}`),
  guide: () => api.get<{ markdown: string }>("/admin/docs/twilio-setup"),
  shiftNow: (employee_id: string) => api.post<{ created: boolean; assignment: { work_date: string; start_time_local: string | null; end_time_local: string | null } }>("/admin/dev/shift-now", { employee_id }),
  runJobs: () => api.post<Record<string, number>>("/admin/dev/run-clock-jobs"),
};

export interface LiveRow {
  assignment_id: string; employee_id: string; on_break?: boolean; employee_name: string | null; role: string | null; shift_time: string;
  status: string; session_id: string | null; review_flags: string[]; in_sms: string; in_gps: string; out_sms: string; out_gps: string;
  delay_in_sec: number | null; delay_out_sec: number | null; delay_highlight: boolean;
  gps: { passed: boolean; code: string; type: string } | null; pending_expires_at: string | null; exceptions: { id: string; code: string }[];
  running_late?: boolean; late_minutes?: number | null; employee_response?: string | null;
}
export interface LiveBoard {
  panels: { venue_id: string; venue_name: string; date: string; timezone: string; rows: LiveRow[] }[];
  counters: { clocked_in: number; pending_gps: number; missing_out: number; rejected_location: number; open_exceptions: number;
    pending_links: { employee_name: string | null; type: string; expires_at: string }[] };
  delay_highlight_min: number; generated_at: string;
}
export interface ReviewItem {
  kind: "EXCEPTION" | "SESSION" | "CORRECTION"; id: string; code: string; status: "OPEN" | "RESOLVED"; type: string;
  employee_id: string; employee_name: string | null; venue_id: string | null; venue_name: string | null; local_date: string | null;
  received_local: string; note: string | null; created_at: string;
}
export type ReviewDetail = Record<string, any> & { kind: ReviewItem["kind"]; item: Record<string, any>; timezone: string; audit: Record<string, any>[] };
export interface SessionRow {
  id: string; employee_id: string; employee_name: string | null; venue_id: string; venue_name: string; work_date: string; status: string;
  review_flags: string[]; in_sms: string; in_gps: string; out_sms: string; out_gps: string; delay_in_sec: number | null; delay_out_sec: number | null;
  payable_in_local: string | null; payable_out_local: string | null; payable_in: string; payable_out: string; original_in: string | null;
  original_out: string | null; edited: boolean; duration: string | null; payable_approved: boolean; approve_blocker: string | null;
}
export interface MyClock {
  status: { status: string; on_break?: boolean; venue_name: string | null; clocked_in_at: string | null; last_result: { type: string; passed: boolean; distance_m: number | null } | null };
  today: { assignment_id: string; venue_name: string; address: string; time: string; role: string | null; instructions: string | null; status: string }[];
  sms_number: string | null; is_preview: boolean;
  recent: { id: string; date: string; venue_name: string; in_sms: string; out_sms: string; gps_in_passed: boolean | null; gps_out_passed: boolean | null; status: string; time_approved: boolean }[];
  exceptions: { id: string; type: string; code: string; status: string; received_local: string; note: string | null }[];
  corrections: import("./timesheets").MyCorrection[];
}
export interface ShiftOption { assignment_id: string; label: string; venue_name: string; work_date: string }

const qs = (o: Record<string, string | undefined>) => {
  const p = Object.entries(o).filter(([, v]) => v);
  return p.length ? `?${new URLSearchParams(p as [string, string][]).toString()}` : "";
};

export const reviewApi = {
  live: (f: Record<string, string | undefined>) => api.get<LiveBoard>(`/admin/timeclock/live${qs(f)}`),
  list: (f: Record<string, string | undefined>) => api.get<ReviewItem[]>(`/admin/review${qs(f)}`),
  detail: (kind: string, id: string) => api.get<ReviewDetail>(`/admin/review/${kind}/${id}`),
  act: (path: string, body: Record<string, unknown>) => api.post<unknown>(`/admin/review/${path}`, body),
  sessions: (f: Record<string, string | undefined>) => api.get<SessionRow[]>(`/admin/sessions${qs(f)}`),
  editPayable: (id: string, body: Record<string, unknown>) => api.patch<unknown>(`/admin/sessions/${id}/payable`, body),
  approve: (id: string) => api.post<unknown>(`/admin/sessions/${id}/approve`),
  approveBulk: (ids: string[]) => api.post<{ results: { id: string; ok: boolean; message: string }[] }>("/admin/sessions/approve-bulk", { ids }),
  myClock: () => api.get<MyClock>("/me/clock"),
  myShifts: () => api.get<ShiftOption[]>("/me/correction-shifts"),
  requestCorrection: (body: Record<string, unknown>) => api.post<unknown>("/me/corrections", body),
};
